import { Dash } from "@/components/Dash";
import { Loading } from "@/components/Loading";
import { Link, useNavigate } from "react-router-dom";
import { useBackOrigin } from "@/hooks/useBackTo";
import { MessageSquare, Clock } from "lucide-react";
import { SortableTH } from "@/components/SortableTH";
import { Pagination } from "@/components/Pagination";
import { useColumnSort } from "@/hooks/useColumnSort";
import { cn } from "@/lib/utils";
import { usePagedList } from "@/hooks/usePagedList";
import { dateOnly, formatDate, formatDateTime } from "@/utils/dates";
import { TicketStatusPill, PriorityBadge } from "@/features/tickets/components/TicketBadges";
import { Notice } from "@/components/Notice";
import { FilterBar } from "@/components/FilterBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { tickets as ticketsApi, type TicketListItem } from "@/features/tickets/api/tickets.api";
import { useCanManageTemplates } from "@/features/tickets/templatePermissions";

type SortKey =
  | "ticketNumber"
  | "subject"
  | "status"
  | "priority"
  | "updatedAt"
  | "dueAt";

const COLUMN_MAP: Record<SortKey, string> = {
  ticketNumber: "ticketNumber",
  subject: "subject",
  status: "status",
  priority: "priority",
  dueAt: "dueAt",
  updatedAt: "lastModificationTime",
};

const TERMINAL_STATUSES = new Set(["Resolved", "Closed"]);

function isOverdue(t: TicketListItem): boolean {
  if (!t.dueAt) return false;
  if (TERMINAL_STATUSES.has(t.status)) return false;
  const due = dateOnly(t.dueAt);
  if (!due) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return due < today;
}

export default function TicketsPage() {
  const navigate = useNavigate();
  const origin = useBackOrigin();
  const canUseTemplates = useCanManageTemplates();
  const { data, loading, loaded, error, forbidden, status, keyword, sortBy, sortDir, setKeyword, setStatus, setPage, setSort } =
    usePagedList<TicketListItem>(ticketsApi.list, {
      initialStatus: "Open",
      initialSortBy: "creationTime",
      initialSortDir: "desc",
      syncUrl: true,
    });
  const filtered = data.items;

  const { thProps } = useColumnSort<SortKey>(COLUMN_MAP, sortBy, sortDir, setSort);

  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">
            Support tickets raised against merchants. Each ticket carries a
            conversation thread of comments and internal notes.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-start">
          {canUseTemplates && (
            <Button asChild variant="outline">
              <Link to="/tickets/templates" state={origin}>Templates</Link>
            </Button>
          )}
          <Button asChild>
            <Link to="/tickets/new" state={origin}>New ticket</Link>
          </Button>
        </div>
      </header>

      {forbidden && (
        <Notice tone="warn">You don't have permission to view tickets (code 50.05).</Notice>
      )}
      {error && !forbidden && <Notice tone="error">{error}</Notice>}
      {!forbidden && !error && (
        <>
          <FilterBar
            tabs={[
              { value: "", label: "All" },
              { value: "Open", label: "Open" },
              { value: "Closed", label: "Closed" },
            ]}
            value={status}
            onValueChange={setStatus}
            search={keyword}
            onSearchChange={setKeyword}
            searchPlaceholder="Search by subject…"
          />

          {loading && !loaded && <Loading>Loading tickets…</Loading>}

          <div className={cn("transition-opacity", loading && loaded && "opacity-60")}>
          {filtered.length === 0 ? (
            loaded && (
              <Notice tone="info">
                No tickets match this filter.
              </Notice>
            )
          ) : (
            <div className="rounded-md border bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTH {...thProps("ticketNumber")}>
                      Ticket
                    </SortableTH>
                    <SortableTH {...thProps("subject")}>
                      Subject
                    </SortableTH>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">
                      Merchant
                    </TableHead>
                    <SortableTH {...thProps("status")}>
                      Status
                    </SortableTH>
                    <SortableTH {...thProps("priority")}>
                      Priority
                    </SortableTH>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">
                      Assigned to
                    </TableHead>
                    <SortableTH {...thProps("dueAt")}>
                      Due
                    </SortableTH>
                    <TableHead className="text-right text-xs font-medium uppercase tracking-wide">
                      Comments
                    </TableHead>
                    <SortableTH {...thProps("updatedAt")} align="right">
                      Updated
                    </SortableTH>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((t) => (
                    <TableRow
                      key={t.id}
                      className={cn("cursor-pointer", isOverdue(t) && "bg-red-50/70 hover:bg-red-50")}
                      onClick={() => navigate(`/tickets/${t.id}`, { state: origin })}
                    >
                      <TableCell>
                        <Link
                          to={`/tickets/${t.id}`}
                          state={origin}
                          className="text-slate-900 hover:underline font-mono text-xs"
                        >
                          #{t.ticketNumber}
                        </Link>
                      </TableCell>
                      <TableCell className="max-w-md">
                        <Link
                          to={`/tickets/${t.id}`}
                          state={origin}
                          title={t.subject}
                          className="block truncate text-slate-900 hover:underline"
                        >
                          {t.subject}
                        </Link>
                        {(t.mainCategory || t.category) && (
                          <div className="truncate text-xs text-slate-500">
                            {[t.mainCategory, t.category].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <Link
                          to={`/merchants/${t.merchantId}`}
                          state={origin}
                          onClick={(e) => e.stopPropagation()}
                          title={t.merchantDbaName ?? undefined}
                          className="block truncate text-slate-900 hover:underline"
                        >
                          {t.merchantDbaName}
                        </Link>
                        <div className="truncate text-xs text-slate-500 font-mono">{t.merchantMid}</div>
                      </TableCell>
                      <TableCell><TicketStatusPill status={t.status} /></TableCell>
                      <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                      <TableCell className="max-w-xs text-slate-600">
                        {t.assignedTo ? (
                          t.assignedTo.name
                            ? <span className="block truncate" title={t.assignedTo.name}>{t.assignedTo.name}</span>
                            : <Dash />
                        ) : (
                          <span className="text-slate-400">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {!t.dueAt ? (
                          <Dash />
                        ) : isOverdue(t) ? (
                          <div className="flex flex-col items-start gap-1">
                            <span className="flex items-center gap-1 font-semibold text-red-600">
                              <Clock className="h-3.5 w-3.5" />
                              {formatDate(t.dueAt)}
                            </span>
                            <Badge
                              variant="destructive"
                              className="px-1.5 py-0 text-[10px] uppercase tracking-wide"
                            >
                              Overdue
                            </Badge>
                          </div>
                        ) : (
                          <span className="text-slate-600">{formatDate(t.dueAt)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {t.commentCount > 0 ? (
                          <span className="inline-flex items-center justify-end gap-1 text-slate-600 tabular-nums">
                            <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
                            {t.commentCount}
                          </span>
                        ) : (
                          <Dash />
                        )}
                      </TableCell>
                      <TableCell className="text-right text-slate-500 text-xs whitespace-nowrap">
                        {formatDateTime(t.updatedAt, { year: "numeric", month: "short", day: "numeric" })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={data} onPageChange={setPage} busy={loading} />
            </div>
          )}
          </div>
        </>
      )}
    </>
  );
}

